import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type IParentState, shouldLoadParent } from "@/redux/modules/parent.js";
import type { IAppState } from "@/redux/reducer.js";
import { ENTITY_TYPE } from "@/settings/settings.js";
import { SampleParentEventsView } from "@/ui-modules/entities/samples/SampleParentEventsView.tsx";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import type { IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import ParentSamplesView from "./ParentSamplesView.js";
import SampleParentSummaryView from "./SampleParentSummaryView.js";

const SampleParentDetailPage: FC = () => {
  const { parentId } = useParams<"parentId">();
  const { parent } = useSelector<IAppState, IParentState>((state) => state.parent);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadParent, parentId);

  useEffect(() => {
    void load();
  }, [load]);

  const getNavItems = (): IRouteNavItemProps[] => {
    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <SampleParentSummaryView />,
      },
      {
        label: "Events",
        route: "events",
        pageComponent: <SampleParentEventsView parentId={Number(parentId)} parent={parent} />,
      },
      {
        label: "Samples",
        route: "samples",
        pageComponent: <ParentSamplesView />,
        default: true,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.SAMPLE_PARENT}
      entityId={toSafeInt(parentId)}
      getDisplayName={() => parent?.name}
      sidebarNavItems={getNavItems()}
    />
  );
};

export default SampleParentDetailPage;
